import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { recentEntries, RECENT_LIMIT } from '@/lib/utils';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowsLeftRight, ArrowLeft, Trash, PencilSimple, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

// Strip the auto-appended "(to <Account>)" suffix from a transfer-out description.
const stripDescSuffix = (desc) => (desc || '').replace(/\s*\(to .+?\)$/, '');

export default function TransferPage() {
  const navigate = useNavigate();
  const canDelete = useCan('transfer', 'delete');
  const canEdit = useCan('transfer', 'edit');
  const [accounts, setAccounts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Default view = today's transfers only. Date picker in the Recent
  // header lets the user jump to any historical day.
  const [filterDate, setFilterDate] = useState('');

  const initialForm = () => ({
    from_account: '', to_account: '', amount: '', description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [form, setForm] = useState(initialForm);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // editing = the transfer_out leg currently being edited (date / description only).
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(() => {
    Promise.all([
      api.get('/finance/accounts'),
      api.get('/finance?type=transfer_out'),
    ]).then(([accRes, txnRes]) => {
      setAccounts(accRes.data);
      // Keep the full set in state; render filters by the picked date.
      setRecent(txnRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      from_account: t.account || '',
      // Backend now enriches transfer_out rows with the paired to_account
      // id, so we can pre-fill the destination directly in the edit form.
      to_account: t.to_account || '',
      amount: String(t.amount || ''),
      description: stripDescSuffix(t.description),
      date: (t.created_at || '').slice(0, 10),
    });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(initialForm());
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editing) {
        // Edit: full field set now supported by the backend (amount,
        // from/to account, date, description). The backend rebuilds the
        // auto-suffix on both legs and reverses + re-applies the account
        // balance moves when money or account fields change.
        if (!form.from_account || !form.to_account || !form.amount) {
          toast.error('Fill all required fields'); return;
        }
        if (form.from_account === form.to_account) {
          toast.error('From and To accounts must be different'); return;
        }
        const amt = parseFloat(form.amount);
        if (isNaN(amt) || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
        await api.put(`/finance/transfers/${editing.transfer_id}`, {
          date: form.date || null,
          description: form.description,
          amount: amt,
          from_account: form.from_account,
          to_account: form.to_account,
        });
        toast.success(`Transfer ${editing.voucher_number || ''} updated`);
        cancelEdit();
        fetchData();
        return;
      }
      // Create
      if (!form.from_account || !form.to_account || !form.amount) {
        toast.error('Fill all required fields');
        return;
      }
      if (form.from_account === form.to_account) {
        toast.error('From and To accounts must be different');
        return;
      }
      const amt = parseFloat(form.amount);
      if (isNaN(amt) || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
      const fromAccObj = accounts.find(a => a.id === form.from_account);
      const allowNegative = (fromAccObj?.type || '').toLowerCase().match(/^(creditor|debtor|capital)$/);
      if (!allowNegative && fromAccObj && (fromAccObj.balance || 0) < amt) {
        toast.error(`Insufficient balance in ${fromAccObj.name}: available ${fmt(fromAccObj.balance)}`);
        return;
      }
      const ts = form.date + 'T' + new Date().toISOString().split('T')[1];
      const { data } = await api.post('/finance/transfer', {
        from_account: form.from_account,
        to_account: form.to_account,
        amount: amt,
        description: form.description,
        created_at: ts,
      });
      toast.success(data.message || 'Transfer recorded');
      setForm(initialForm());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const accountName = (id) => accounts.find(a => a.id === id)?.name || '-';
  const fromAcc = accounts.find(a => a.id === form.from_account);

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/finance/${confirmDelete.id}`);
      toast.success('Transfer deleted');
      setConfirmDelete(null);
      if (editing && editing.id === confirmDelete.id) cancelEdit();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="transfer-page">
      <div>
        <button
          onClick={() => navigate('/finance')}
          className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A]"
          data-testid="back-to-balance-sheet"
        >
          <ArrowLeft size={12} /> Back to Balance Sheet
        </button>
      </div>

      {/* Form Card — same form for create + edit */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm max-w-3xl" data-testid="transfer-form-card">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }} data-testid="transfer-form-heading">
                {editing ? <>Edit <span className="text-[#3B82F6]">{editing.voucher_number || ''}</span></> : 'New Transfer'}
              </h2>
              <p className="text-xs text-[#52525B] mt-0.5">
                {editing ? 'All fields editable — account balances will reverse + re-apply on save.' : 'Move money between accounts.'}
              </p>
            </div>
            {editing && (
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="rounded-lg text-[#52525B]" data-testid="cancel-edit-transfer">
                <X size={14} className="mr-1" /> Cancel
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" data-testid="transfer-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A1A1AA]">&#8377;</span>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" className="rounded-lg pl-7 font-mono" data-testid="transfer-amount-input" />
              </div>
              {fromAcc && form.amount && (() => {
                const after = (fromAcc.balance || 0) - (parseFloat(form.amount) || 0);
                const allowNeg = (fromAcc.type || '').toLowerCase().match(/^(creditor|debtor|capital)$/);
                return (
                  <p className="text-xs mt-1" data-testid="transfer-balance-preview">
                    <span className="text-[#52525B]">Available: {fmt(fromAcc.balance)} &rarr; After: </span>
                    <span className={after < 0 ? (allowNeg ? 'text-[#3B82F6] font-semibold' : 'text-[#EF4444] font-semibold') : 'text-[#52525B]'}>
                      {fmt(after)}
                      {after < 0 && allowNeg ? ' (will run negative — allowed)' : ''}
                    </span>
                  </p>
                );
              })()}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">From Account *</Label>
              <Select value={form.from_account} onValueChange={v => setForm({ ...form, from_account: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="transfer-from-select"><SelectValue placeholder="Select source account..." /></SelectTrigger>
                <SelectContent>
                  {/* Apply the credit-card rule: hide credit cards with a
                      positive (over-paid) balance — only show ones at zero
                      or negative balance. Creditor accounts always shown.
                      All other account types unaffected. */}
                  {accounts.filter(a => a.type !== 'credit_card' || (a.balance || 0) <= 0).map(a =>
                    <SelectItem key={a.id} value={a.id}>{a.name} — {fmt(a.balance)}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">To Account *</Label>
              <Select value={form.to_account} onValueChange={v => setForm({ ...form, to_account: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="transfer-to-select"><SelectValue placeholder="Select destination account..." /></SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => a.id !== form.from_account && (a.type !== 'credit_card' || (a.balance || 0) <= 0)).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} — {fmt(a.balance)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Description</Label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional note" className="rounded-lg mt-1" data-testid="transfer-desc-input" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            {editing && (
              <Button variant="outline" onClick={cancelEdit} className="rounded-xl border-[#E4E4E7]" data-testid="transfer-cancel-btn">Cancel</Button>
            )}
            {!editing && (
              <Button variant="outline" onClick={() => setForm(initialForm())} className="rounded-xl border-[#E4E4E7]" data-testid="transfer-reset-btn">Reset</Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#F59E0B] text-white hover:bg-[#D97706] hover:text-white rounded-xl"
              data-testid="confirm-transfer-btn"
            >
              <ArrowsLeftRight size={14} className="mr-1" weight="bold" />
              {submitting ? 'Saving...' : (editing ? 'Save Changes' : 'Transfer Funds')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Transfers */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid="recent-transfers-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Recent Transfers</h2>
              <p className="text-xs text-[#52525B]" data-testid="transfer-recent-caption">{filterDate ? `Showing transfers dated ${new Date(filterDate).toLocaleDateString('en-IN')}` : `Showing last ${RECENT_LIMIT} transfers`}</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="h-8 rounded-lg text-xs w-36"
                data-testid="transfer-recent-date-filter"
              />
<Button variant="ghost" size="sm" className={`h-8 px-2 text-xs ${!filterDate ? 'bg-[#EFF6FF] text-[#1D4ED8] font-semibold' : 'text-[#52525B]'}`}
                onClick={() => { setFilterDate(''); }}
                data-testid="transfer-recent-last-btn">
                Last 25
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-[#52525B]"
                onClick={() => setFilterDate(new Date().toISOString().split('T')[0])}
                data-testid="transfer-recent-today-btn">
                Today
              </Button>
            </div>
          </div>
          {(() => {
            const displayed = recentEntries(recent, filterDate);
            if (displayed.length === 0) {
              return <p className="text-sm text-[#A1A1AA] italic text-center py-6" data-testid="transfer-recent-empty">{filterDate ? `No transfers on ${new Date(filterDate).toLocaleDateString('en-IN')}` : 'No transfers yet'}</p>;
            }
            return (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Voucher</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">From</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">To</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Detail</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Amount</TableHead>
                  {(canEdit || canDelete) && <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map(t => (
                  <TableRow key={t.id} className={`border-[#E4E4E7] group ${editing?.id === t.id ? 'bg-[#EFF6FF]' : ''}`}>
                    <TableCell className="text-xs text-[#52525B]">{new Date(t.created_at).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      {t.voucher_number
                        ? <span className="rounded-md text-[10px] px-2 py-0.5 bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]">{t.voucher_number}</span>
                        : <span className="text-[10px] text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs font-medium">{accountName(t.account)}</TableCell>
                    <TableCell className="text-xs font-medium">
                      {/* Primary: backend-enriched to_account_name (always
                          accurate, independent of description). Fallback:
                          parse the auto-appended "(to <Name>)" suffix from
                          the description so legacy rows still render. */}
                      {t.to_account_name
                        || (t.to_account && accountName(t.to_account))
                        || ((t.description || '').match(/\(to\s+(.+?)\)\s*$/) || [])[1]
                        || <span className="text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-[#52525B] max-w-[400px] truncate" title={t.description}>
                      {stripDescSuffix(t.description) || '-'}
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono font-semibold text-[#F59E0B]">{fmt(t.amount)}</TableCell>
                    {(canEdit || canDelete) && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <button
                              onClick={() => startEdit(t)}
                              className="p-1.5 rounded-md hover:bg-[#EFF6FF] text-[#52525B] hover:text-[#3B82F6] opacity-60 group-hover:opacity-100 transition-opacity"
                              title="Edit transfer"
                              data-testid={`edit-transfer-${t.id}`}
                            >
                              <PencilSimple size={13} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setConfirmDelete(t)}
                              className="p-1.5 rounded-md hover:bg-[#FEE2E2] text-[#52525B] hover:text-[#DC2626] opacity-60 group-hover:opacity-100 transition-opacity"
                              title="Delete transfer"
                              data-testid={`delete-transfer-${t.id}`}
                            >
                              <Trash size={13} />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Delete Confirm — paired transfer_in/transfer_out are both removed by the backend */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this transfer?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  <span className="font-medium text-[#0A0A0A]">{stripDescSuffix(confirmDelete.description)}</span> &middot; {fmt(confirmDelete.amount)}
                  <br />Both legs of the transfer will be removed and account balances reverted. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="cancel-delete-transfer">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="confirm-delete-transfer">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
